import React from 'react';
import { Container, Typography, Box, Grid, Paper, Button, useTheme, useMediaQuery } from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import BuildIcon from '@mui/icons-material/Build';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import BrushIcon from '@mui/icons-material/Brush';
import { useLanguageContext } from '../context/LanguageContext';
import { translations } from '../translations';
import SkillsSection from '../components/SkillsSection';
import Interests from '../components/Interests';
import PersonalInfo from '../components/PersonalInfo';

function Home() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { language } = useLanguageContext();
  const t = translations[language];

  const iconMap = {
    code: <CodeIcon />,
    build: <BuildIcon />,
    lightbulb: <LightbulbIcon />,
    brush: <BrushIcon />,
  };

  const skills = t.home.skills.map(skill => ({
    ...skill,
    icon: iconMap[skill.icon] || <CodeIcon />
  }));

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          mt: { xs: 4, sm: 8 }, 
          mb: { xs: 4, sm: 6 }, 
          px: { xs: 2, sm: 0 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '100vh',
          py: 4
        }}
      >
        <Typography 
          variant={isMobile ? "h3" : "h2"} 
          component="h1" 
          gutterBottom 
          align="center"
          sx={{
            wordBreak: 'break-word',
            fontSize: {
              xs: '2rem',
              sm: '3rem',
              md: '3.75rem'
            },
            fontWeight: 700,
            textAlign: 'center',
            mb: 2
          }}
        >
          {t.personalInfo.name}
        </Typography>
        
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          color="primary" 
          gutterBottom 
          align="center"
          sx={{
            mb: 3,
            textAlign: 'center'
          }}
        >
          {t.personalInfo.title}
        </Typography>
        
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ 
            mb: 4,
            px: { xs: 2, sm: 4, md: 8 },
            textAlign: 'center',
            fontStyle: 'italic',
            fontWeight: 500
          }}
        >
          {t.personalInfo.tagline}
        </Typography>

        <Button
          component="a"
          href="/resume/Ulrich_Ndeloko_Fall_2026_Co-op_Resume.pdf"
          download
          variant="contained"
          color="primary"
          sx={{
            mt: { xs: 3, sm: 3.5 },
            mb: { xs: 3, sm: 3.5 },
            borderRadius: 3,
            px: 3,
            py: 1.25,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
              transform: 'translateY(-1px)',
            },
          }}
        >
          {t.home.downloadMyResume}
        </Button>

        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          gutterBottom 
          align="center"
          sx={{ mt: { xs: 4, sm: 8 }, mb: { xs: 3, sm: 4 } }}
        >
          {t.home.whatIDo}
        </Typography>
        
        <Grid container spacing={{ xs: 2, sm: 4 }}>
          {skills.map((skill, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: { xs: 2, sm: 3 }, 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-5px)'
                  }
                }}
              >
                <Box 
                  sx={{ 
                    color: 'primary.main',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'rotate(10deg)',
                    },
                  }}
                >
                  {skill.icon}
                </Box>
                <Typography 
                  variant="h6" 
                  component="h3" 
                  gutterBottom 
                  align="center"
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                >
                  {skill.title}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  align="center"
                >
                  {skill.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper
          elevation={3}
          sx={{
            p: 4,
            mt: 4,
            maxWidth: 800,
            mx: 'auto',
            backgroundColor: 'background.paper',
          }}
        >
          <Typography
            variant="body1"
            paragraph
            sx={{
              whiteSpace: 'pre-line',
              lineHeight: 1.8,
              textAlign: 'justify',
              '&:first-line': {
                initialLetter: '1.5 2',
                fontWeight: 'bold',
                fontSize: '2em',
                lineHeight: 1,
                marginRight: '0.25em',
                color: 'primary.main'
              }
            }}
          >
            {t.personalInfo.summary}
          </Typography>
        </Paper>

        <SkillsSection />
        <PersonalInfo />
        <Interests />
      </Box>
    </Container>
  );
}

export default Home;
